/**
 * @file configuration.js
 * @description Configuration component for managing client settings.
 * @component ClientConfiguration
 * @exports ClientConfiguration - Main component for displaying and managing client settings.
 */
import React, { useState, useEffect } from "react";
import {
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    Container,
    Box,
    Alert,
} from "@mui/material";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Grid from "@mui/material/Grid2";
import CardComponent from "../common/components/CardComponent";
import { getclientconfiguration, clientconfiguration } from "../services/api"; // Ensure you have both APIs
import { refreshClientTimeZone } from "../utils/clienttimezone";

/**
 * The time zone set here is not a display preference — it decides which
 * calendar day every dated figure in the product is counted on. It drives:
 *
 *   • the date-range presets on every dashboard
 *   • the day × hour heatmaps
 *   • timestamp rendering on schedules, messages and processes
 *   • when report schedules fire and what window each report covers
 *   • server-side bucketing of payments, opt-outs, compliance and promises
 *
 * Stored as dbo.clientconfig.ClientTimeZone (capital Z). The API aliases it
 * to ClientTimezone on the way out; the read below accepts either so a change
 * to that alias can't silently drop the setting back to UTC.
 */
const readZone = (data) =>
    data?.ClientTimeZone ?? data?.ClientTimezone ?? "UTC";

const ClientConfiguration = () => {
    const [companyName, setCompanyName] = useState("");
    const [primaryColor, setPrimaryColor] = useState("#000000");
    const [secondaryColor, setSecondaryColor] = useState("#FFFFFF");
    const [configId, setConfigId] = useState(null);
    const [font, setFont] = useState("Arial");
    const [ClientTimezone, setClientTimeZone] = useState("UTC");
    const [publishCustomerData, setPublishCustomerData] = useState(false);
    const [approveConversation, setApproveConversation] = useState(false);
    const [approveAIResponse, setApproveAIResponse] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [saving, setSaving] = useState(false);

    // One place that maps a config payload onto state. The fetch and the
    // post-save refetch were doing this twice, which is how the two copies
    // drift — the save path had already lost nothing yet, but it's the kind
    // of duplication where one branch quietly stops setting a field.
    const applyConfig = (data) => {
        if (!data) return;
        setConfigId(data.id);
        setCompanyName(data.companyName || "");
        setPrimaryColor(data.primaryColor || "#000000");
        setSecondaryColor(data.secondaryColor || "#FFFFFF");
        setFont(data.font || "Arial");
        setPublishCustomerData(data.publishCustomerData === 1); // 1/0 → boolean
        setApproveConversation(data.approveConversation === 1);
        setApproveAIResponse(data.approveAIResponse === 1);
        setClientTimeZone(readZone(data));
    };

    // Fetch the configuration data when the component mounts
    useEffect(() => {
        const fetchConfiguration = async () => {
            try {
                setLoadError("");
                applyConfig(await getclientconfiguration());
            } catch (error) {
                console.error("Error fetching configuration:", error);
                // Surfaced rather than logged only: on failure configId stays
                // null, which disables Save, and a permanently dead button
                // with nothing on screen reads as a broken page.
                setLoadError("Couldn't load the current configuration. Reload to try again.");
            }
        };
        fetchConfiguration();
    }, []);

    const handleSaveConfiguration = async () => {
        const jsonData = {
            companyName,
            primaryColor,
            secondaryColor,
            font,
            publishCustomerData: publishCustomerData ? 1 : 0,
            approveConversation: approveConversation ? 1 : 0,
            approveAIResponse: approveAIResponse ? 1 : 0,
            ClientTimezone,
        };
        try {
            setSaving(true);
            await clientconfiguration(configId, jsonData);
            applyConfig(await getclientconfiguration());

            // Drop the cached zone so mounted dashboards pick the new one up
            // and refetch on their own. Without this the app keeps bucketing
            // on the old zone until the next full page load — which is what
            // the old "Please refresh the page" alert was working around.
            await refreshClientTimeZone();

            alert("Configuration updated successfully.");
        } catch (error) {
            console.error("Error updating configuration", error);
            alert("Failed to update configuration.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container maxWidth={false} disableGutters>
            {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
            <Grid container spacing={3}>
                <Grid size={{ sm: 12, md: 8 }}>
                    <CardComponent title="Theme Configuration">
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                flexDirection: "column",
                            }}
                        >
                            <TextField
                                label="Company Name"
                                fullWidth
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                size="small"
                            />
                            <TextField
                                label="Primary Color"
                                type="color"
                                fullWidth
                                value={primaryColor}
                                onChange={(e) =>
                                    setPrimaryColor(e.target.value)
                                }
                                size="small"
                            />
                            <TextField
                                label="Secondary Color"
                                type="color"
                                fullWidth
                                value={secondaryColor}
                                onChange={(e) =>
                                    setSecondaryColor(e.target.value)
                                }
                                size="small"
                            />
                            <TextField
                                label="Font"
                                fullWidth
                                value={font}
                                onChange={(e) => setFont(e.target.value)}
                                size="small"
                            />
                        </Box>
                    </CardComponent>
                </Grid>
                <Grid size={{ sm: 12, md: 4 }}>
                    <CardComponent title="Automation">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={publishCustomerData}
                                    onChange={(e) =>
                                        setPublishCustomerData(e.target.checked)
                                    }
                                />
                            }
                            label="Publish Customer Data"
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={approveConversation}
                                    onChange={(e) =>
                                        setApproveConversation(e.target.checked)
                                    }
                                />
                            }
                            label="Approve Conversation"
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={approveAIResponse}
                                    onChange={(e) =>
                                        setApproveAIResponse(e.target.checked)
                                    }
                                />
                            }
                            label="Approve AI Response"
                            fullWidth
                        />
                    </CardComponent>
                    <CardComponent sx={{ mt: 2 }} title="Time Settings">
                        <FormControl sx={{ m: 0, mt: 2, minWidth: 150 }} size="small">
                            <InputLabel id="time-zone-label">
                                Time Zone
                            </InputLabel>
                            <Select
                                labelId="time-zone"
                                id="time-zone"
                                value={ClientTimezone}
                                label="Time Zone"
                                onChange={(e) => setClientTimeZone(e.target.value)}
                            >
                                <MenuItem value="UTC">
                                    UTC (Coordinated Universal Time)
                                </MenuItem>
                                <MenuItem value="PT">
                                    Pacific Time (PT) - America/Los_Angeles
                                </MenuItem>
                                <MenuItem value="MT">
                                    Mountain Time (MT) - America/Denver
                                </MenuItem>
                                <MenuItem value="CT">
                                    Central Time (CT) - America/Chicago
                                </MenuItem>
                                <MenuItem value="ET">
                                    Eastern Time (ET) - America/New_York
                                </MenuItem>
                                <MenuItem value="AKT">
                                    Alaska Time (AKT) - America/Anchorage
                                </MenuItem>
                                <MenuItem value="HST">
                                    Hawaii Time (HST) - Pacific/Honolulu
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <Box sx={{ mt: 1.5, fontSize: 12, color: "text.secondary", lineHeight: 1.5 }}>
                            Sets the calendar day every dated figure is counted on —
                            dashboard ranges, heatmap hours, schedule timestamps, and
                            when scheduled reports run.
                        </Box>
                    </CardComponent>
                </Grid>
                <Grid size={{ sm: 12 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveConfiguration}
                        disabled={!configId || saving}
                    >
                        {saving ? "Saving…" : "Save Configuration"}
                    </Button>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ClientConfiguration;